import { yupResolver } from '@hookform/resolvers/yup';
import { Megaphone } from '@phosphor-icons/react';
import { EmployeeModel, EmployeeMutationInput } from 'api-hooks/employee';
import formSetErrors from 'common/helpers/form-setError';
import CloakContainer from 'components/cloak-container';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import { Form, FormState } from 'components/elements';
import Button from 'components/elements/buttons/button';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { EmptyTabStyles } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import EmployeeInformationForm from './employee-information-form';
import EmployeePromotionTable from './employee-promotion-table';
import { EmployeeFormSchema, EmployeeFormType } from './form-type';
import useDialogPromotionForm from '../hooks/use-dialog-promotion-form';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  employee?: EmployeeModel;
  onSubmit: (
    input: EmployeeMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<EmployeeModel | undefined>;
}

enum EmployeeTabEnum {
  detail = 'detail',
  history = 'history',
}

export default function EmployeeForm(props: Props) {
  const { t } = useTranslation();
  const { employee } = props;
  const { asPath } = useRouter();

  const [tab, setTab] = React.useState(EmployeeTabEnum.detail);

  const initialValues = useGetDefaultValues(employee);

  const resolver = yupResolver(EmployeeFormSchema());
  const methods = useForm<EmployeeFormType>({
    resolver,
    mode: 'onChange',
    defaultValues: initialValues,
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        const input = { ...values, roles: values.roles.map((role) => role.id) };
        await props.onSubmit(input as EmployeeMutationInput, methods as any);
      } catch (e: any) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('employees');
  const { onDelete, isLoading: isDeleteLoading } = useDestroyAction(
    'employees',
    employee?.id,
  );

  const customViewName =
    employee && [t('common:employee'), employee.name].join(' - ');

  const { onClickEmployeePromotion } = useDialogPromotionForm(
    employee?.id || '',
  );

  const onRenderComponent = React.useMemo(() => {
    if (!employee) return undefined;

    return (
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginRight: 16,
        }}
      >
        <Button
          buttonType="secondary"
          startEnhancer={(color, size) => (
            <Megaphone size={size} color={color} />
          )}
          onClick={onClickEmployeePromotion(undefined)}
        >
          {t('common:promotion')}
        </Button>
      </div>
    );
  }, [employee, onClickEmployeePromotion, t]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!employee}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            onDelete={onDelete}
            data={employee}
            noDelete={editable || !permission.delete}
            isDeleteLoading={isDeleteLoading}
            noEdit={!permission.update}
            title={
              employee
                ? customViewName ?? t('common:employee')
                : t('common:add_extra', { extra: t('common:employee') })
            }
            onRenderComponent={onRenderComponent}
          />
        )}
      </FormState>
      {!!employee && (
        <Tabs
          activeKey={tab}
          items={[
            {
              key: EmployeeTabEnum.detail,
              component: <></>,
              title: t('common:detail'),
            },
            {
              key: EmployeeTabEnum.history,
              component: <></>,
              title: t('common:promotion_history'),
            },
          ]}
          onChangeActive={({ activeKey }) => {
            setTab(activeKey);
          }}
          overrideStyles={EmptyTabStyles}
        />
      )}
      <CloakContainer isActive={tab === EmployeeTabEnum.detail}>
        <div>
          <EmployeeInformationForm hasData={!!employee} />
          <Separator gap={24} />
        </div>
      </CloakContainer>
      <CloakContainer isActive={tab === EmployeeTabEnum.history}>
        {!!employee && <EmployeePromotionTable employee={employee} />}
      </CloakContainer>
    </Form>
  );
}
